import { useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAIAssistant } from "../hooks/useAIAssistant";
import DesktopPageHeader from "./DesktopPageHeader";
import QuickReplyCategories from "./QuickReplyCategories";
import almaceniaAvatar from "../assets/alma-red.svg";
import TypingIndicator from "./TypingIndicator";
import VoiceInput from "./VoiceInput";

export const AIAssistantTab = ({ resto }) => {
  const {
    conversations,
    conversationsLoading,
    currentConversationId,
    currentConversationTitle,
    messages,
    sendLoading,
    sendError,
    isStreaming,
    activeTool,
    getConversationsList,
    getConversationById,
    deleteConversationById,
    startNewChat,
    sendMessage,
  } = useAIAssistant();

  const [input, setInput] = useState("");
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    getConversationsList();
  }, [getConversationsList]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeTool]);

  const handleSend = useCallback((text) => {
    if (!text || text.length > 2000 || isStreaming) return;
    setInput("");
    sendMessage(text);
  }, [isStreaming, sendMessage]);

  const handleSelectConversation = (id) => {
    if (id === currentConversationId) {
      setIsHistoryOpen(false);
      return;
    }
    setIsHistoryOpen(false);
    getConversationById(id);
  };

  const handleNewChat = () => {
    setIsHistoryOpen(false);
    startNewChat();
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    await deleteConversationById(id);
  };

  const handleQuickReply = (userMessage) => {
    if (isStreaming) return;
    sendMessage(userMessage);
  };

  const restoName = resto?.name || "";
  const avatar = resto?.brand || resto?.avatar || resto?.cover || "";

  return (
    <OuterLayout>
      <BannerWrapper>
        <DesktopPageHeader img={avatar} alt={restoName} title={restoName} />
      </BannerWrapper>

      <Layout>
        <SideToolbar>
          <HistoryToggleButton
            onClick={() => setIsHistoryOpen((v) => !v)}
            aria-label={isHistoryOpen ? "Cerrar historial" : "Abrir historial"}
            $active={isHistoryOpen}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6h16" />
              <path d="M4 12h16" />
              <path d="M4 18h16" />
            </svg>
          </HistoryToggleButton>
          <NewChatIconButton
            onClick={handleNewChat}
            disabled={isStreaming}
            aria-label="Nuevo chat"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
          </NewChatIconButton>
        </SideToolbar>

        <ChatAreaWrapper>
          {isHistoryOpen && <HistoryOverlay onClick={() => setIsHistoryOpen(false)} />}
          <HistoryPanel $open={isHistoryOpen}>
            <HistoryPanelHeader>
              <HistoryPanelTitle>Historial</HistoryPanelTitle>
              <HistoryPanelClose onClick={() => setIsHistoryOpen(false)}>×</HistoryPanelClose>
            </HistoryPanelHeader>
            <ConversationsList>
              {conversationsLoading && conversations.length === 0 && (
                <EmptyState>Cargando...</EmptyState>
              )}
              {conversations.length === 0 && !conversationsLoading && (
                <EmptyState>No hay conversaciones</EmptyState>
              )}
              {conversations.map((conv) => (
                <ConversationItem
                  key={conv._id}
                  $active={conv._id === currentConversationId}
                  onClick={() => handleSelectConversation(conv._id)}
                >
                  <ConversationTitle>{conv.title || "Sin título"}</ConversationTitle>
                  <ConversationDate>
                    {conv.updatedAt
                      ? new Date(conv.updatedAt).toLocaleDateString()
                      : ""}
                  </ConversationDate>
                  <DeleteButton onClick={(e) => handleDelete(e, conv._id)}>
                    ×
                  </DeleteButton>
                </ConversationItem>
              ))}
            </ConversationsList>
          </HistoryPanel>

          <ChatArea>
            <ChatHeader>
              <ChatTitle>
                {currentConversationTitle || "ALMA"}
              </ChatTitle>
              {activeTool && <ToolBadge>{activeTool}</ToolBadge>}
            </ChatHeader>

            <MessagesContainer>
              {messages.length === 0 && (
                <>
                  <WelcomeMessage>
                    <WelcomeAvatar src={almaceniaAvatar} alt="ALMA" />
                    <h3>¡Hola! Soy ALMA</h3>
                    <p>
                      Tu socIA digital. Estoy aquí para ayudarte a vender más, fidelizar clientes y hacer crecer tu almacén. Selecciona un tema o pregúntame lo que necesites.
                    </p>
                  </WelcomeMessage>
                  <QuickRepliesContainer>
                    <QuickReplyCategories
                      onSelectQuestion={handleQuickReply}
                      disabled={isStreaming}
                    />
                  </QuickRepliesContainer>
                </>
              )}
              {messages.map((msg) => (
                <MessageRow key={msg.id} $role={msg.role}>
                  <MessageBubble $role={msg.role}>
                    {msg.role === "assistant" ? (
                      msg.isStreaming && !msg.text ? (
                        <TypingIndicator />
                      ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.text || ""}
                        </ReactMarkdown>
                      )
                    ) : msg.role === "function" || msg.role === "functionCall" ? (
                      <pre
                        style={{
                          margin: 0,
                          background: "#e8e8e8",
                          padding: "0.5rem",
                          borderRadius: "6px",
                          fontSize: "0.8em",
                        }}
                      >
                        <code>{msg.text || "Function call"}</code>
                      </pre>
                    ) : (
                      msg.text || ""
                    )}
                    {msg.isStreaming && msg.text && <CursorBlink>|</CursorBlink>}
                  </MessageBubble>
                </MessageRow>
              ))}
              <div ref={messagesEndRef} />
            </MessagesContainer>

            {sendError && <ErrorBanner>{sendError}</ErrorBanner>}

            <InputArea>
              <VoiceInput
                value={input}
                onChange={setInput}
                onSend={handleSend}
                disabled={isStreaming}
                placeholder="Pregúntame sobre tu almacén..."
                rows={2}
              />
            </InputArea>
          </ChatArea>
        </ChatAreaWrapper>
      </Layout>
    </OuterLayout>
  );
};

/* ========== ESTILOS ========== */

const OuterLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 500px;
  box-sizing: border-box;
  gap: 0.8rem;
`;

const BannerWrapper = styled.div`
  width: 100%;

  > div {
    max-width: 100% !important;
  }

  > div > div {
    margin-bottom: 0 !important;
  }
`;

const Layout = styled.div`
  display: flex;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    display: grid;
    grid-template-columns: 1fr;
  }
`;

const SideToolbar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.4rem;
  background: #fff;
  border: 1px solid #eaeaea;
  border-radius: 12px;
  width: 56px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const ToolbarButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.85;
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const HistoryToggleButton = styled(ToolbarButton)`
  svg {
    transition: transform 0.25s ease;
    transform: ${({ $active }) => ($active ? "rotate(90deg)" : "rotate(0deg)")};
  }
`;

const NewChatIconButton = styled(ToolbarButton)`
  background: #fe0103;
`;

const ChatAreaWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  overflow: hidden;
  border-radius: 12px;
`;

const HistoryPanel = styled.aside`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  background: #fff;
  border: 1px solid #eaeaea;
  border-radius: 0 12px 12px 0;
  box-shadow: 4px 0 16px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 20;
  transform: ${({ $open }) => ($open ? "translateX(0)" : "translateX(-110%)")};
  transition: transform 0.25s ease;
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};

  @media (max-width: 1024px) {
    display: none;
  }
`;

const HistoryOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.08);
  z-index: 10;
  border-radius: 12px;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const HistoryPanelHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #eaeaea;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const HistoryPanelTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const HistoryPanelClose = styled.button`
  background: transparent;
  border: none;
  color: #666;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  padding: 0.2rem;

  &:hover {
    color: #000;
  }
`;

const ConversationsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
`;

const ConversationItem = styled.div`
  padding: 0.7rem 0.6rem;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 0.3rem;
  position: relative;
  background: ${({ $active }) => ($active ? "#f5f5f5" : "transparent")};
  border: 1px solid ${({ $active }) => ($active ? "#000" : "transparent")};

  &:hover {
    background: #f5f5f5;
  }
`;

const ConversationTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 1.2rem;
`;

const ConversationDate = styled.div`
  font-size: 0.8rem;
  color: #888;
  margin-top: 0.2rem;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  background: transparent;
  border: none;
  color: #888;
  font-size: 1rem;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #c00;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #888;
  font-size: 0.95rem;
  padding: 1rem;
`;

const ChatArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #fff;
  border: 1px solid #eaeaea;
  border-radius: 12px;
  overflow: hidden;
`;

const ChatHeader = styled.div`
  padding: 0.8rem 1rem;
  border-bottom: 1px solid #eaeaea;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const ChatTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const ToolBadge = styled.span`
  font-size: 0.85rem;
  color: #666;
  background: #f5f5f5;
  padding: 0.35rem 0.7rem;
  border-radius: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const WelcomeAvatar = styled.img`
  width: 80px;
  height: auto;
  margin-bottom: 0.5rem;
`;

const WelcomeMessage = styled.div`
  text-align: center;
  color: #555;
  margin: auto;
  max-width: 400px;

  h3 {
    margin: 0 0 0.5rem;
    font-size: 1.3rem;
  }

  p {
    margin: 0;
    font-size: 1.05rem;
    line-height: 1.45;
  }
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${({ $role }) => ($role === "user" ? "flex-end" : "flex-start")};
`;

const MessageBubble = styled.div`
  max-width: 70%;
  padding: 0.8rem 1.1rem;
  border-radius: 16px;
  font-size: 1.05rem;
  line-height: 1.45;
  background: ${({ $role }) => ($role === "user" ? "#000" : "#f5f5f5")};
  color: ${({ $role }) => ($role === "user" ? "#fff" : "#333")};
  white-space: pre-wrap;
  word-break: break-word;
`;

const CursorBlink = styled.span`
  animation: blink 1s step-start infinite;

  @keyframes blink {
    50% {
      opacity: 0;
    }
  }
`;

const ErrorBanner = styled.div`
  padding: 0.7rem 1rem;
  background: #ffeaea;
  color: #c00;
  font-size: 0.95rem;
  border-top: 1px solid #ffcccc;
`;

const InputArea = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.8rem 1rem;
  border-top: 1px solid #eaeaea;
  background: #fff;
`;

const QuickRepliesContainer = styled.div`
  padding: 0 1rem 1rem;
`;
